import { useEffect } from 'react';

import { PopupScenario, usePredictionsStore } from '@/entities/prediction';
import { useResultsStore } from '@/entities/result';
import { useSessionStore } from '@/entities/session';
import { formatScore } from '@/shared/lib';

import { hasRank, resultContent } from '../model/result-content';
import { RESULT_VARIANTS } from '../model/variants';

import { ResultCounter, ResultModal } from './ResultModal';

/**
 * Какой из ЭТИХ поп-апов сейчас на экране: итог недели и месяца
 * либо дневные итоги Прогнозов.
 *
 * Порядок: приветствие новичку → итог периода → дневные итоги Прогнозов.
 * Итог периода вперёд, потому что он крупнее и случается раз в неделю,
 * а дневной поп-ап сжигается только при закрытии и дождётся следующего
 * открытия.
 *
 * Финал сезона сюда не относится: у него своя модалка и свой хост
 * (`widgets/season-final-modal`) — виджет не может импортировать виджет,
 * поэтому composition живёт в `app`, рядом с приветствием и подпиской.
 * Очередь при этом общая: закрытие одинаково отмечается через
 * `POST /results/seen`, а порядок («Финал раньше месячного») держит сервер.
 */
export function ResultModalHost() {
  const popup = usePredictionsStore((state) => state.popup);
  const loadPopup = usePredictionsStore((state) => state.loadPopup);
  const dismissPopup = usePredictionsStore((state) => state.dismissPopup);

  const periodResult = useResultsStore((state) => state.pending);
  const loadResults = useResultsStore((state) => state.load);
  const dismissResult = useResultsStore((state) => state.dismiss);

  const isNewUser = useSessionStore((state) => state.isNewUser);

  useEffect(() => {
    void loadPopup();
    void loadResults();
  }, [loadPopup, loadResults]);

  // Новичку показываем приветствие: две полноэкранные панели наложились бы
  // друг на друга, а итогов у него всё равно ещё нет
  if (isNewUser) return null;

  if (periodResult) {
    // Этот хост рисует итоги С МЕСТОМ — недельный и месячный. Финал
    // сезона места не имеет (жеребьёвка, а не рейтинг) и уезжает
    // в SeasonFinalModalHost; отличаем именно по `rank`, а не по `kind`,
    // потому что каталог раскладок упирается ровно в место: без него
    // заголовок пришлось бы брать из макета, где стоит «Ты на 999 месте».
    //
    // Возвращаем null, а НЕ проваливаемся к дневному поп-апу ниже:
    // иначе на экране оказались бы две полноэкранные панели разом
    if (!hasRank(periodResult)) return null;

    const content = resultContent(periodResult);
    return (
      <ResultModal
        testId="result-modal"
        onClose={dismissResult}
        shape={content.shape}
        image={content.image}
        title={content.title}
        body={content.body}
        score={content.score}
        caption={content.caption}
      />
    );
  }

  if (popup === null) return null;

  const isSuccess = popup.scenario === PopupScenario.SUCCESS;
  const fallback = RESULT_VARIANTS[isSuccess ? 'daily-uspeh' : 'daily-oops'];

  return (
    <ResultModal
      testId="result-modal"
      onClose={dismissPopup}
      shape={fallback.shape}
      image={fallback.image}
      title={
        popup.title === undefined || popup.title === ''
          ? fallback.title
          : popup.title
      }
      body={
        popup.body === undefined || popup.body === ''
          ? fallback.body
          : popup.body
      }
      score={
        isSuccess && popup.totalPoints !== undefined ? (
          <ResultCounter icon="points" value={formatScore(popup.totalPoints)} />
        ) : undefined
      }
    />
  );
}
